import getConfig from 'next/config';
import { type AxiosError, isAxiosError } from 'axios';

import { HOST_QUERY_KEY, metaApiInstance, TOKEN_SCOPE } from '@kc-console/context';
import { META_STATUS_CODE, STATUS_CODE } from '@kc-console/core';

import { getRequestUrlData } from '../../utils/getRequestUrlData';
import { parsePathname } from '../../utils/parsePathname';
import { getServerTraceHttpTimingOptions } from '../../utils/serverTrace/store';

import type { ServiceAppState, TokenScope } from '@kc-console/context';
import type { MetaStatusCode } from '@kc-console/core';
import type { Guard } from '../../types';

/**
 * 접근하려는 URL에 해당하는 페이지의 정보를 취득하는 가드입니다.
 * @description
 * - 접근하려는 페이지의 serviceKeyword를 URL을 파싱하여 취득합니다.
 * - 접근하려는 페이지의 scope을 meta api로부터 질의하여 취득합니다.
 * - meta api로 질의하는 postUrlValidation은 리전과 무관합니다.
 */
export const determineBaseInfoGuard: Guard<ServiceAppState> = async ({ context, queryClient }) => {
  const { postUrlValidation } = metaApiInstance({
    ...getServerTraceHttpTimingOptions(context.req, 'config'),
  });
  const { urlObj } = getRequestUrlData(context);
  const { serviceKeyword, routePath } = parsePathname(urlObj.pathname);

  try {
    const { scope } = await queryClient.fetchQuery({
      queryKey: [HOST_QUERY_KEY.GET_SCOPE],
      queryFn: () => postUrlValidation({ serviceKeyword, path: routePath }),
    });

    return { serviceKeyword, scope, status: STATUS_CODE.OK };
  } catch (error) {
    /**
     * 로컬 개발 편의를 위한 스킵 코드.
     * 로컬에서 서비스 개발 시 serviceMeta.route를 등록하지 않고 개발할 수 있도록 가드를 통과시키는 설정.
     */
    if (process.env.NODE_ENV === 'development' && !getConfig()?.serverRuntimeConfig?.isBaguni) {
      const envScope = process.env.LOCAL_DEV_SCOPE || '';
      const isEnvScopeExist = Object.values(TOKEN_SCOPE).map(String).includes(envScope);
      const localDevScope = isEnvScopeExist ? (envScope as TokenScope) : TOKEN_SCOPE.PROJECT;
      return { serviceKeyword, scope: localDevScope, status: STATUS_CODE.OK };
    }

    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<{ statusCode: MetaStatusCode }>;
      const metaStatusCode = axiosError.response?.data.statusCode;

      /**
       * NOTE. postUrlValidation은 모든 리전에서 route의 존재여부를 판단한다.
       * 모든 리전에서 유효하지 않은 route이므로 가드를 종료하고 404화면을 노출한다.
       */
      if (metaStatusCode === META_STATUS_CODE.NOT_FOUND_SERVICE) {
        return { status: STATUS_CODE.NOT_FOUND };
      } else if (metaStatusCode === META_STATUS_CODE.NOT_FOUND_ROUTE) {
        return { status: STATUS_CODE.NOT_FOUND };
      }

      /**
       * FIXME. meta API의 상태 코드 개편 작업으로 인해 퍼블릭과 공공의 에러 코드가 달라졌음.
       * 4/16 퍼블릭 배포 예정이며 공공 배포 일정은 미정이므로, 둘 다 404로 시작하는 경우 NOT_FOUND처리한다.
       * 추후 공공도 개편된 상태 코드 작업이 반영되면 개편된 에러 코드로 처리하도록 정리한다.
       */
      if (String(metaStatusCode).startsWith('404')) {
        return { status: STATUS_CODE.NOT_FOUND };
      }
    }

    return { status: STATUS_CODE.INTERNAL_SERVER_ERROR };
  }
};

determineBaseInfoGuard.displayName = 'determineBaseInfoGuard';
